import { MutableRefObject, useCallback, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePostCmsEmailIssuers } from "api/cms";
import { Button, FormSelect, Input } from "components";
import { Textarea } from "components/Textarea/Textarea";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { InfoIcon } from "lucide-react";
import { useGetContractIdData } from "providers/ContractIdProvider";
import { useKeycloak } from "providers/KeycloakProvider";
import { Controller, useForm } from "react-hook-form";
import { contactIssuersSchema } from "./ContactSchemas";
import { useRelevantIssuers } from "./hooks/useRelevantIssuers";
import { IssuerSelect } from "./IssuerSelect";
import { Radio } from "./Select";
import { HOLDINGS_SETTINGS, Option, VALID_ASSET_TYPES } from "./types";

interface EmailIssuersModalProps {
  modalInitialFocusRef: MutableRefObject<null>;
  onClose: () => void;
}

/**
 * What the user actually fills in. Sender identity is deliberately absent: it
 * comes from the session, is shown read-only, and is merged in on submit.
 */
export interface issuerFormData {
  holdingsSetting: Option<HOLDINGS_SETTINGS>;
  assetType: Option<VALID_ASSET_TYPES>;
  text: string;
  subject: string;
  externalIds: string[];
}

const LineBreakText = ({ text }: { text: string }) => {
  return (
    <div className="flex items-center mt-6 mb-1">
      <div className=" w-full bg-primary-20 h-[1px]"></div>
      <span className="mx-1 text-sm text-primary-40 whitespace-nowrap">
        {text}
      </span>
      <div className=" w-full bg-primary-20 h-[1px]"></div>
    </div>
  );
};

/**
 * The single switch that takes "Kontakta fondbolag" live: it enables the
 * submit button and removes the preview banner. Everything behind it is
 * already wired — the form submits, `usePostCmsEmailIssuers` posts to the CMS
 * route, the modal closes on success.
 *
 * Driven by `REACT_APP_CONTACT_ISSUERS_SEND_ENABLED` so it can be turned on
 * per environment without a code change. Anything other than the exact string
 * "true" — including the variable being absent — leaves sending off, so an
 * environment that never heard of this flag cannot accidentally start
 * emailing fund companies.
 *
 * Note CRA inlines this at build time: changing the env file needs a dev
 * server restart, not just a reload.
 */
const SENDING_ENABLED =
  process.env.REACT_APP_CONTACT_ISSUERS_SEND_ENABLED === "true";

const MailIssuersModalContent = ({ onClose }: EmailIssuersModalProps) => {
  const { t } = useModifiedTranslation();
  const { userProfile } = useKeycloak();
  const { sendEmail, isLoading: isSending } = usePostCmsEmailIssuers();
  const { selectedContact } = useGetContractIdData();

  /**
   * The session's answer to "who is sending this". Both parts must be present:
   * a message with no reply address is as broken as one with no sender name,
   * and neither is something the user can fix from here.
   */
  const sender = useMemo(() => {
    const name = [userProfile?.firstName, userProfile?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const email = userProfile?.email?.trim() ?? "";
    return name && email ? { name, email } : null;
  }, [userProfile?.firstName, userProfile?.lastName, userProfile?.email]);

  /**
   * The FA contact the portal is acting for — the customer the message is sent
   * about, which with an impersonated representee is someone other than the
   * signed-in user. Shown in its own field so the two names cannot be mistaken
   * for each other.
   */
  const fromContact = selectedContact?.userName ?? "";

  const holdingsOptions: Option<HOLDINGS_SETTINGS>[] = [
    {
      id: HOLDINGS_SETTINGS.PORTFOLIO,
      label: t("contactModal.fields.holdings"),
    },
    {
      id: HOLDINGS_SETTINGS.ALL,
      label: t("contactModal.fields.allFunds"),
    },
  ];

  const assetTypeOptions: Option<VALID_ASSET_TYPES>[] = [
    {
      id: VALID_ASSET_TYPES.ALL,
      label: t("assetTypes.all"),
    },
    {
      id: VALID_ASSET_TYPES.SWEDEN_STOCKS,
      label: t("assetTypes.swedishStocks"),
    },
    {
      id: VALID_ASSET_TYPES.GLOBAL_STOCKS,
      label: t("assetTypes.globalStocks"),
    },
    {
      id: VALID_ASSET_TYPES.ALTERNATIVE,
      label: t("assetTypes.alternativeInvestments"),
    },
    {
      id: VALID_ASSET_TYPES.NOMINAL,
      label: t("assetTypes.nominalInterestRate"),
    },
    {
      id: VALID_ASSET_TYPES.REALA,
      label: t("assetTypes.realInterestRates"),
    },
  ];

  const resolver = zodResolver(contactIssuersSchema);

  const { watch, control, register, handleSubmit } = useForm<issuerFormData>({
    resolver: resolver,
    mode: "all",
    defaultValues: {
      // Opens on "Dina innehav": the fund companies a user actually holds are
      // the ones they are most likely to write to, and it means the picker is
      // on screen from the start rather than behind a choice they have to make
      // before the form shows them anything.
      holdingsSetting: holdingsOptions[0],
      assetType: assetTypeOptions[0],
      text: "",
      subject: "",
      externalIds: [],
    },
  });

  const holdingsSetting = watch("holdingsSetting")?.id;
  const assetTypeSetting = watch("assetType")?.id;
  const issuerOptions = useRelevantIssuers({
    holdingsSetting,
    assetTypeSetting,
  });

  const onSubmit = useCallback(
    async (formData: issuerFormData) => {
      // Guarded by the disabled button too; this is the belt for that pair of
      // braces, since a submit can also arrive by keyboard.
      if (!sender) return;
      // `sendEmail` is `mutateAsync`, so a failed send throws here and the
      // modal stays open with the user's message intact. The error toast is
      // raised by the hook's onError.
      try {
        await sendEmail({
          ...formData,
          fromContact,
          senderName: sender.name,
          senderEmail: sender.email,
        });
      } catch {
        return;
      }
      onClose();
    },
    [sendEmail, onClose, sender, fromContact]
  );

  const canSend = SENDING_ENABLED && sender !== null;

  return (
    <div>
      {/* While sending is off this stays a preview: submitting is a no-op, so a
          stray Enter keypress in a text field cannot reach the API. */}
      <form
        onSubmit={canSend ? handleSubmit(onSubmit) : (e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-8 w-[600px]">
          <div className="flex flex-col gap-4">
            {!SENDING_ENABLED && (
              <div className="flex items-center px-4 py-3 w-full rounded-lg bg-secondary-100 text-white-100">
                <span>
                  <InfoIcon className="mr-4 w-10 h-10 shrink-0 text-white-100" />
                </span>
                <div>
                  <h3 className="mb-1 font-semibold text-headline-3 text-white-100">
                    {t("contactModal.comingSoon.header")}
                  </h3>
                  <p className="text-sm text-balance text-white-100">
                    {t("contactModal.comingSoon.explainer")}
                  </p>
                </div>
              </div>
            )}
            <LineBreakText text={t("contactModal.whoToContact")} />
            <Controller
              control={control}
              name="holdingsSetting"
              render={({ field: { onChange, value } }) => (
                <Radio
                  name="holdingsSetting"
                  label={t("contactModal.fields.whatToContact")}
                  options={holdingsOptions}
                  selected={value}
                  onSelect={onChange}
                />
              )}
            />
            <FormSelect
              text="sm"
              fontWeight="font-normal"
              name="assetType"
              control={control}
              label={t("contactModal.fields.assetType")}
              options={assetTypeOptions}
            />
            {holdingsSetting && (
              <Controller
                control={control}
                name="externalIds"
                render={({ field: { onChange, value } }) => (
                  <div>
                    <label className="text-sm font-normal">
                      {t("contactModal.fields.pickIssuers")}
                    </label>
                    <IssuerSelect
                      issuers={issuerOptions.data}
                      hasIssuers={issuerOptions.hasIssuers}
                      loading={issuerOptions.loading}
                      selected={value}
                      onSelect={onChange}
                    />
                  </div>
                )}
              />
            )}
            <LineBreakText text={t("contactModal.yourInfo")} />
            {/* Always disabled, and deliberately outside the form: these are
                facts about the session, not input. They keep their labels
                because "Arvid Gårdebo" and "Kund: Bolaget AB" are two different
                kinds of name and the form has to say which is which — but they
                are not registered, so they can never produce a validation error
                the user has no way to fix. An unusable profile is caught below
                instead. */}
            <Input
              label={t("contactModal.fields.fromContact")}
              disabled
              readOnly
              type="text"
              value={fromContact}
            />
            <Input
              label={t("contactModal.fields.name")}
              disabled
              readOnly
              type="text"
              value={sender?.name ?? ""}
            />
            <Input
              label={t("contactModal.fields.email")}
              disabled
              readOnly
              type="email"
              value={sender?.email ?? ""}
            />
            {!sender && (
              // Nothing to do from inside the modal: the sender is fixed, so an
              // incomplete profile is a dead end rather than a field to fix.
              // Say so right under the empty fields it explains.
              <p className="px-4 py-3 text-sm rounded-lg bg-dark-red-20 text-dark-red-100">
                {t("contactModal.sender.missingProfile")}
              </p>
            )}
            <Input
              label={t("contactModal.fields.subject")}
              required
              type="text"
              {...register("subject")}
            />
            <Textarea
              label={t("contactModal.fields.text")}
              className="min-h-[140px]"
              required
              {...register("text")}
            />
          </div>
          {/* Enabled once live, bar the in-flight state and an unusable
              profile: field errors on click explain themselves, a dead button
              does not. */}
          <Button
            disabled={!canSend}
            isLoading={isSending}
            type="submit"
            variant="Primary"
            size="md"
            rounded="lg"
          >
            <span>
              {SENDING_ENABLED
                ? t("contactModal.send")
                : t("contactModal.comingSoon.label")}
            </span>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MailIssuersModalContent;
